import React, { ReactNode, RefObject, useRef, useState } from 'react'
import { useRouter } from '~/hooks/useRouter'
import { FormattedMessage } from 'react-intl'
import styles from './ConfiguratorPageShell.module.css'
import { OrderProgress } from '~/components/OrderProgress/OrderProgress'
import { ProductGallery } from '~/components/ProductPage/productTypeComponents/ProductGallery'
import { InfoBar } from '~/components/InfoBar/InfoBar'
import { productInfoBarContent } from '~/components/InfoBar/ProductInfoBarContent'
import { Breadcrumb } from '~/components/Breadcrumb/Breadcrumb'
import { productTypes } from '~/components/ProductTypesList/productTypes'
import { ServiceOptionsProvider } from '~/context/serviceOptionsContext'
import { ServiceOptions } from '~/components/ProductPage/productTypeComponents/ServiceOptions'

interface ConfiguratorPageShellProps {
  /** Left column: 3D viewer node */
  viewer: ReactNode
  /** Render thumbnail URLs (1–3). Empty / omitted = no strip rendered. */
  renderThumbnails?: string[]

  /** Right card: title element(s) */
  heading: ReactNode
  /** Right card: price + action buttons area */
  priceSection: ReactNode
  /** Right card: page-specific controls (dimensions, color, drawers…) */
  controls: ReactNode

  /** Images for the ProductGallery below the fold. Omit to hide gallery. */
  renderImages?: string[]
  /** External ref to gallery section (optional, shell creates its own if omitted) */
  galleryRef?: RefObject<HTMLDivElement>

  /** Show OrderProgress bar. Default true. */
  showOrderProgress?: boolean
  /** Variant passed to OrderProgress. Default none. */
  orderProgressVariant?: 'table'

  /** Show InfoBar. Default true. */
  showInfoBar?: boolean

  /** Cart modal, share modal, etc. – rendered in the below-fold container */
  modals?: ReactNode

  /** Extra content rendered after InfoBar (e.g. Banner, Grid) */
  belowFoldExtra?: ReactNode
}

export const ConfiguratorPageShell: React.FC<ConfiguratorPageShellProps> = ({
  viewer,
  renderThumbnails = [],
  heading,
  priceSection,
  controls,
  renderImages,
  galleryRef: externalGalleryRef,
  showOrderProgress = true,
  orderProgressVariant,
  showInfoBar = true,
  modals,
  belowFoldExtra,
}) => {
  const [activeThumbnail, setActiveThumbnail] = useState(0)
  const internalGalleryRef = useRef<HTMLDivElement>(null)
  const galleryRef = externalGalleryRef ?? internalGalleryRef
  const router = useRouter()
  const debugQ = router.query?.debug
  const isDebug = debugQ === 'true' || debugQ === '1'
  const strippedPath = router.pathname.replace('/[locale]', '')
  const productType = productTypes.find(({ link }) => link === strippedPath)
  const breadcrumbItems = [
    { label: 'homepage.configurator.breadcrumb.1', link: '/' },
    { label: 'homepage.configurator.breadcrumb.2', link: '/catalog' },
    ...(productType ? [{ label: productType.name, link: strippedPath }] : []),
  ]

  const scrollToGallery = () => {
    galleryRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <ServiceOptionsProvider>
      <div className={styles.configuratorContainer}>
        <div className={styles.mobileBreadcrumb}>
          <Breadcrumb items={breadcrumbItems} />
        </div>
        <div className={styles.configuratorContent}>
          {/* Left: thumbnail strip + 3D viewer */}
          <div className={styles.leftColumn}>
            <div className={styles.viewerStage}>
              {renderThumbnails.length > 0 && !isDebug && (
                <div className={styles.thumbnailStrip}>
                  {renderThumbnails.map((src, idx) => {
                    const isActive = activeThumbnail === idx
                    return (
                      <button
                        key={src}
                        type="button"
                        className={
                          isActive
                            ? `${styles.thumbnail} ${styles.thumbnailActive}`
                            : styles.thumbnail
                        }
                        onMouseEnter={() => setActiveThumbnail(idx)}
                        onFocus={() => setActiveThumbnail(idx)}
                        onClick={scrollToGallery}
                        aria-label={`Open render ${idx + 1}`}
                        aria-pressed={isActive}
                      >
                        <img src={src} alt={`Render preview ${idx + 1}`} />
                      </button>
                    )
                  })}
                </div>
              )}
              <div className={styles.viewerWrapper}>{viewer}</div>
            </div>
            {renderThumbnails.length > 0 && (
              <button
                type="button"
                className={styles.mobileThumbnail}
                onClick={scrollToGallery}
                aria-label="View renders"
              >
                <img src={renderThumbnails[0]} alt="View renders" />
              </button>
            )}
          </div>


          {/* Right: details card */}
          {!isDebug && (
          <div className={styles.detailsCardWrapper}>
            <div className={styles.detailsCard}>
              <div className={styles.detailsCardHeader}>
                {heading}
                {priceSection}
              </div>
              <div className={styles.detailsCardControls}>
                <ServiceOptions />
                {controls}
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Below the fold */}
      <div className={styles.belowFoldContainer}>
        {showOrderProgress && (
          <>
            <br />
            <OrderProgress
              activeStep={0}
              {...(orderProgressVariant ? { variant: orderProgressVariant } : {})}
            />
            <br />
          </>
        )}

        {renderImages && renderImages.length > 0 && (
          <div ref={galleryRef}>
            <ProductGallery
              configuration={{ type: 'gallery', images: renderImages }}
            />
            <a
              href="https://www.instagram.com/dulap.studio"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.instagramLink}
            >
              <FormattedMessage id="homepage.social.realProjects" />
              &nbsp;→
            </a>
          </div>
        )}

        {showInfoBar && (
          <>
            <br />
            <InfoBar items={productInfoBarContent} />
            <br />
          </>
        )}

        {belowFoldExtra}

        {modals}
      </div>
    </ServiceOptionsProvider>
  )
}
